// Pure helpers for the Ask page: an answer's steps, text and moments from its content blocks (as /ai/chats saves
// them), the moment links answers cite and the chat list's date groups.

import { fmt } from "../../pages/videos/scenes/sceneModel";

const isCall = (b) => /tool_use$/.test(b.type || "");

/** a tool result's JSON (the MCP tools answer with one text block), or null */
export function resultData(result) {
  if (!result || !Array.isArray(result.content)) return null;
  const text = result.content.map((c) => c.text || "").join("");
  try {
    return JSON.parse(text);
  } catch (e) {
    return null;
  }
}

/** project_id -> project_name, from what the tools returned */
function videoNames(results) {
  const names = {};
  const add = (o) => o && o.project_id !== undefined && o.project_name && (names[o.project_id] = o.project_name);
  results.forEach((data) => {
    if (Array.isArray(data)) data.forEach(add);
    else if (data) {
      add(data);
      (data.videos || []).forEach(add);
      (data.moments || []).forEach(add);
    }
  });
  return names;
}

const momentKey = (m) => m.project_id + ":" + m.start_time;

/**
 * What an answer shows: its steps (the tool calls, in order), its text, and the moments it is about (those it showed
 * with show_moments, with the words to mark in them).
 * @param content the answer's blocks
 * @param running whether the answer is still being written: a call without a result is running, else stopped
 * @returns {steps: [{tool, server, input, inputs, data, status, projects}], text, moments, highlight, names}
 */
export function parseTurn(content, running = false) {
  const blocks = Array.isArray(content) ? content : [];
  const results = {};
  blocks.filter((b) => b.tool_use_id).forEach((b) => (results[b.tool_use_id] = b));
  const names = videoNames(Object.values(results).map(resultData));

  const steps = [];
  const moments = [];
  const seen = new Set();
  let highlight = [];
  blocks.filter(isCall).forEach((b) => {
    const result = results[b.id];
    const status = result ? (result.is_error ? "error" : "done") : running ? "running" : "stopped";
    const input = b.input || {};
    const data = resultData(result);
    if (b.name === "show_moments") {
      if (status === "done" && data) {
        (data.moments || []).forEach((m) => {
          if (!seen.has(momentKey(m))) moments.push(m);
          seen.add(momentKey(m));
        });
        highlight = [...new Set([...highlight, ...(input.highlight || [])])];
      }
      return;
    }
    const project = input.project_id !== undefined ? names[input.project_id] || String(input.project_id) : null;
    const prev = steps[steps.length - 1];
    // reading the segments of the moments found, one call per video: one step
    if (b.name === "get_transcripts" && segment(input) && prev && prev.tool === "get_transcripts" && segment(prev.input)) {
      prev.inputs.push(input);
      if (project && !prev.projects.includes(project)) prev.projects.push(project);
      prev.status = mergeStatus(prev.status, status);
      if (status === "running") prev.current = project;
      return;
    }
    steps.push({
      tool: b.name,
      server: b.type === "server_tool_use",
      input,
      inputs: [input],
      data,
      result,
      status,
      projects: project ? [project] : [],
      current: status === "running" ? project : null,
    });
  });

  const text = blocks
    .filter((b) => b.type === "text")
    .map((b) => b.text || "")
    .join("")
    .trim();
  return { steps, text, moments, highlight, names };
}

const segment = (input) => input.start_time !== undefined || input.end_time !== undefined;
const RANK = { running: 0, error: 1, stopped: 2, done: 3 };
const mergeStatus = (a, b) => (RANK[a] <= RANK[b] ? a : b);

const quoted = (words) => words.map((w) => "“" + w + "”").join(", ");
const listOf = (names) => names.join(", ");

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    return url || "";
  }
}

const countOf = (t, n, one, many) => (n === 1 ? t(one) : t(many, { count: n }));

/** "5 matches in 2 videos" of a search_moments result */
function matchesDetail(t, data) {
  if (!Array.isArray(data)) return null;
  if (!data.length) return t("No matches");
  const videos = new Set(data.map((m) => m.project_id)).size;
  return t("{{matches}} in {{videos}}", {
    matches: countOf(t, data.length, "1 match", "{{count}} matches"),
    videos: countOf(t, videos, "1 video", "{{count}} videos"),
  });
}

/**
 * The words of a step: done (past tense), doing (while it runs), short (in the answer's header) and detail (what it
 * found, or null).
 * @param t i18next's t
 */
export function describeStep(step, t) {
  const { tool, input, data } = step;
  const video = step.projects[0] || input.video_name || null;
  const scope = video || t("all your videos");
  let done, doing, short, detail = null;
  if (tool === "search_moments") {
    const phrases = input.phrases || [];
    const people = input.people || [];
    const speakers = input.speakers || [];
    if (phrases.length || input.query) {
      const words = quoted(phrases.length ? phrases : [input.query]);
      if (phrases.length) {
        done = t("Searched transcripts in {{scope}} for {{words}}", { scope, words });
        doing = t("Searching transcripts in {{scope}} for {{words}}", { scope, words });
        short = t("Searched transcripts for {{words}}", { words });
      } else {
        done = t("Searched {{scope}} for {{words}}", { scope, words });
        doing = t("Searching {{scope}} for {{words}}", { scope, words });
        short = t("Searched for {{words}}", { words });
      }
      if (speakers.length) {
        [done, doing] = [done, doing].map((s) => t("{{search}}, said by {{people}}", { search: s, people: listOf(speakers) }));
      } else if (people.length) {
        [done, doing] = [done, doing].map((s) => t("{{search}}, with {{people}}", { search: s, people: listOf(people) }));
      }
    } else if (speakers.length) {
      done = t("Read the lines of {{people}} in {{scope}}", { people: listOf(speakers), scope });
      doing = t("Reading the lines of {{people}} in {{scope}}", { people: listOf(speakers), scope });
      short = t("Read the lines of {{people}}", { people: listOf(speakers) });
    } else {
      done = t("Looked for {{people}} in {{scope}}", { people: listOf(people), scope });
      doing = t("Looking for {{people}} in {{scope}}", { people: listOf(people), scope });
      short = t("Looked for {{people}}", { people: listOf(people) });
    }
    detail = matchesDetail(t, data);
  } else if (tool === "search_videos") {
    if (input.query) {
      const words = quoted([input.query]);
      done = short = t("Searched your videos for {{words}}", { words });
      doing = t("Searching your videos for {{words}}", { words });
    } else {
      done = short = t("Listed your videos");
      doing = t("Listing your videos");
    }
    if (data && Array.isArray(data.videos)) detail = countOf(t, data.videos.length, "1 video", "{{count}} videos");
  } else if (tool === "get_video_overview") {
    done = short = t("Read the overview of {{video}}", { video });
    doing = t("Reading the overview of {{video}}", { video });
  } else if (tool === "get_scenes") {
    done = short = t("Read the scenes of {{video}}", { video });
    doing = t("Reading the scenes of {{video}}", { video });
    if (data && Array.isArray(data.scenes)) detail = countOf(t, data.scenes.length, "1 scene", "{{count}} scenes");
  } else if (tool === "get_transcripts") {
    if (segment(input)) {
      done = short = t("Read the matching segments");
      doing = t("Reading the matching segments in {{video}}", { video: step.current || video });
      detail = listOf(step.projects) || null;
    } else {
      done = short = t("Read the transcript of {{video}}", { video });
      doing = t("Reading the transcript of {{video}}", { video });
      if (data && Array.isArray(data.sentences)) detail = countOf(t, data.sentences.length, "1 line", "{{count}} lines");
    }
  } else if (tool === "get_frames") {
    done = short = t("Looked at {{video}}", { video });
    doing = t("Looking at {{video}}", { video });
    const frames = step.result && Array.isArray(step.result.content) ? step.result.content.filter((c) => c.type === "image").length : 0;
    if (frames) detail = countOf(t, frames, "1 frame", "{{count}} frames");
  } else if (tool === "web_search") {
    const words = quoted([input.query || ""]);
    done = short = t("Searched the web for {{words}}", { words });
    doing = t("Searching the web for {{words}}", { words });
    const found = step.result && Array.isArray(step.result.content) ? step.result.content.length : 0;
    if (found) detail = countOf(t, found, "1 result", "{{count}} results");
  } else if (tool === "web_fetch") {
    const site = hostOf(input.url);
    done = short = t("Read {{site}}", { site });
    doing = t("Reading {{site}}", { site });
  } else if (step.server) {
    done = short = t("Ran code");
    doing = t("Running code");
  } else {
    done = short = t("Used {{tool}}", { tool });
    doing = t("Using {{tool}}", { tool });
  }
  if (step.status === "error") detail = t("Didn’t work");
  else if (step.status === "stopped") detail = t("Stopped");
  return { done, doing, short, detail };
}

// the steps the header sums an answer up with, the first found of: a search of moments, of the web, of the videos
const SEARCHES = [(s) => s.tool === "search_moments", (s) => s.tool === "web_search", (s) => s.tool === "search_videos" && s.input.query];

/**
 * The line in an answer's header: what it is doing while it runs, else its main search and what it found.
 * @param status the answer's: running, done, stopped or error
 */
export function headerLine(turn, status, t) {
  const { steps, text } = turn;
  if (status === "running") {
    const current = [...steps].reverse().find((s) => s.status === "running");
    if (current) return describeStep(current, t).doing + "…";
    return text ? t("Writing the answer…") : steps.length ? t("Thinking…") : t("Looking at your question…");
  }
  if (status === "stopped") return t("Stopped");
  const done = steps.filter((s) => s.status === "done");
  const main = SEARCHES.reduce((found, is) => found || done.find(is), null) || steps[0];
  if (!main) return null;
  const d = describeStep(main, t);
  return d.detail ? d.short + " · " + d.detail : d.short;
}

const CITE = /^(?:https?:\/\/[^/]+)?\/project\/(\d+)\/scenes\?t=(\d+(?:\.\d+)?)$/;

/** {projectId, time} of a link to a moment, as answers cite them ([0:19](/project/2006/scenes?t=19)), else null */
export function parseCite(href) {
  const m = CITE.exec(href || "");
  return m ? { projectId: Number(m[1]), time: Number(m[2]) } : null;
}

/** an answer as plain text, its citations as [0:19] */
export function copyText(text) {
  return (text || "")
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (all, label, href) => (parseCite(href) ? "[" + label + "]" : label + " (" + href + ")"))
    .replace(/\*\*([^*]+)\*\*/g, "$1");
}

/** the moment of moments around time in the project, else the first one starting after it, else null */
export function momentAt(moments, projectId, time) {
  const mine = moments.filter((m) => m.project_id === projectId);
  return mine.find((m) => time >= m.start_time - 0.5 && time <= m.end_time) || mine.find((m) => m.start_time >= time) || null;
}

/** what a moment card shows: who speaks and what they say (the first line saying what was searched, else the first) */
export function momentLine(m) {
  const lines = m.lines || [];
  const line = lines.find((l) => l.hit) || lines[0] || null;
  return { speaker: (line && line.speaker) || (m.people || [])[0] || "", text: line ? line.text : "", scene: m.scene ? m.scene.headline : "" };
}

export const timeRange = (a, b) => fmt(a) + " - " + fmt(b);

// midnight days before d, in calendar days: some days have 23 or 25 hours
const startOfDay = (d, before = 0) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - before).getTime();

/**
 * chats (most recent first) in groups by when they were last updated, in the user's time zone:
 * [{key: today|yesterday|week|older, chats}], the empty ones left out
 */
export function groupChats(chats, now = new Date()) {
  const [today, yesterday, week] = [0, 1, 7].map((n) => startOfDay(now, n));
  const groups = { today: [], yesterday: [], week: [], older: [] };
  chats.forEach((c) => {
    const at = new Date(c.updated_at || c.created_at).getTime();
    const key = at >= today ? "today" : at >= yesterday ? "yesterday" : at >= week ? "week" : "older";
    groups[key].push(c);
  });
  return Object.keys(groups)
    .filter((k) => groups[k].length)
    .map((key) => ({ key, chats: groups[key] }));
}

/** messages with those of update (from its first index on) in place of theirs */
export function mergeMessages(messages, update) {
  if (!update || !update.length) return messages;
  const from = update[0].index;
  return [...messages.filter((m) => m.index < from), ...update];
}

/**
 * A moment as MomentPreview shows a /search/nlq result: its lines matched to the video's transcript sentences (by
 * start time), so that the transcript marks them.
 * @param transcript the video's sentences [{sentence_id, start_time, end_time}], or null while unknown
 */
export function toResult(m, transcript) {
  const byStart = new Map((transcript || []).map((s) => [Math.round(s.start_time * 100), s]));
  const lines = (m.lines || []).map((l, i, all) => {
    const s = byStart.get(Math.round(l.start * 100));
    return {
      sentence_id: s ? s.sentence_id : "l" + i,
      start: l.start,
      end: s ? s.end_time : all[i + 1] ? all[i + 1].start : m.end_time,
      speaker: l.speaker || "",
      text: l.text,
      hit: !!l.hit,
    };
  });
  return {
    id: momentKey(m),
    project_id: m.project_id,
    start: m.start_time,
    end: m.end_time,
    type: m.type,
    label: "match",
    scene: m.scene
      ? { index: m.scene.index, title: m.scene.headline, start: m.scene.start_time, end: m.scene.end_time }
      : { index: -1, title: null, start: m.start_time, end: m.end_time },
    lines,
    events: (m.events || []).map((e) => ({ kind: e.kind, start: e.time, end: e.time, text: e.text })),
    on_screen: (m.on_screen || []).map((o) => ({ name: o.name, start: o.start_time, end: o.end_time })),
    people: m.people || [],
  };
}

/** a moment at time in a project when the answer's moments have none there: the 10 s from it */
export const momentFrom = (projectId, time, names) => ({
  project_id: projectId,
  project_name: names[projectId],
  start_time: time,
  end_time: time + 10,
  type: "section",
  lines: [],
});

export { momentKey };
